<template>
  <v-container
    id="floating-widget"
    class="widget-container pa-0 bg-grey-lighten-5"
    ref="widgetContainer"
    :class="{ open: chatInterfaceOpen, shadow: chatInterfaceOpen }"
    fluid
  >
    <div
      v-if="!chatInterfaceOpen && !chatInterfaceMinimized && !loading"
      class="button-container hoverable"
    >
      <v-img
        v-if="buttonImage"
        :src="buttonImage"
        height="100"
        @click="createConversation"
        class="custom-image-button"
      />
      <RobotsComponent
        v-else
        :index="animationIndex"
        height="200"
        style="cursor: pointer"
        @click="createConversation"
      />
      <!--v-btn v-else class="bg-primary" @click="createConversation">
        Chat
      </v-btn-->
    </div>
    <div v-if="!chatInterfaceOpen && chatInterfaceMinimized">
      <v-btn
        @click="restoreChat"
        icon
        size="large"
        class="restore-conversation-button"
        ><IconComponent color="primary" name="message"
      /></v-btn>
    </div>
    <div
      v-if="!chatInterfaceOpen && loading && !chatInterfaceMinimized"
      class="d-flex justify-center align-center"
      style="height: 100%"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <div
      v-if="chatInterfaceOpen && !chatInterfaceMinimized"
      class="chat-interface"
    >
      <div class="top-area" :style="'background-color: ' + widgetColor">
        <v-row align="center">
          <v-col cols="1">
            <RobotsComponent :index="animationIndex" :height="50" :width="50" />
          </v-col>
          <v-col cols="4">
            <span style="color: white; margin-left: 16px; opacity: 60%"
              ><v-tooltip activator="parent" location="top">{{
                spokeId
              }}</v-tooltip
              >{{ spokeName ? spokeName : spokeId }}</span
            ><v-progress-circular
              v-if="loading"
              indeterminate
              color="white"
              size="16"
              width="3"
              class="ml-4"
            ></v-progress-circular>
          </v-col>
          <v-col cols="2">
            <v-btn
              @click="minimizeChat"
              icon
              size="x-small"
              class="minimize-button hoverable"
              ><IconComponent color="primary" name="minimize"
            /></v-btn>
          </v-col>
          <v-col cols="2">
            <v-btn
              @click="closeChat"
              icon
              size="x-small"
              class="close-button hoverable"
              ><IconComponent color="primary" name="close"
            /></v-btn>
          </v-col>
        </v-row>
      </div>

      <v-container class="messages-container" ref="messagesContainer">
        <v-row
          ><v-col>
            <div>
              <div
                v-for="message in messages"
                :key="message.content"
                class="mb-2 px-3 py-2 rounded-lg message"
                :class="[
                  message.role === 'assistant'
                    ? 'bg-white text-dark float-left text-left'
                    : 'bg-primary text-white float-right text-right',
                  message.role === 'assistant' ? 'mr-2' : 'ml-2',
                ]"
              >
                <vue-showdown
                  class="markdown-container"
                  :markdown="
                    message.content?.replace(/<hidden>.*?<\/hidden>/gs, '')
                  "
                  :extensions="['targetlink']"
                  flavor="github"
                />
                <div class="clearfix"></div>
              </div>
            </div>
            <div v-if="sendingMessage">
              <div
                class="mb-2 px-3 py-2 rounded-lg small-message bg-white text-dark float-left text-center mr-2"
              >
                <div class="loading">
                  <span>·</span><span>·</span><span>·</span>
                </div>
              </div>
            </div>
          </v-col></v-row
        >
      </v-container>

      <div class="bottom-area bg-grey-lighten-5">
        <v-row>
          <v-col cols="10">
            <v-textarea
              v-model="inputMessage"
              @keydown.enter.prevent="
                ($event.shiftKey && $event.keyCode === 13) || sendMessage()
              "
              @keydown.shift.enter="$event.target.value += '\n'"
              placeholder="Type your message here..."
              rows="2"
              hide-details="auto"
              auto-grow
              class="text-input"
            ></v-textarea>
          </v-col>
          <v-col cols="2" class="d-flex flex-column align-center pa-0">
            <v-btn
              @click="sendMessage"
              :disabled="sendingMessage || loading"
              icon
              elevation="2"
              size="small"
              class="send-button bg-secondary hoverable"
              ><IconComponent size="20" name="send"
            /></v-btn>
          </v-col>
        </v-row>
        <v-row class="ma-0"
          ><v-col class="pa-0 d-flex flex-row-reverse" cols="12">
            <div v-if="chatInterfaceOpen" class="powered-by">
              <a
                href="https://www.spokesai.com"
                target="_blank"
                class="powered-by-link"
              >
                Powered by SpokesAI
                <img
                  src="@/assets/favicon.png"
                  alt="Spokes"
                  class="powered-by-icon"
                />
              </a>
            </div> </v-col
        ></v-row>
      </div>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import IconComponent from "@/components/IconComponent.vue";
import RobotsComponent from "./RobotsComponent.vue";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "@/firebase";

const apiClient = axios.create({
  headers: {
    "API-Key": "",
    "Content-Type": "application/json",
  },
});

export default {
  name: "ConversationWidget",
  components: {
    IconComponent,
    RobotsComponent,
  },
  props: {
    propApiKey: {
      type: String,
      default: null,
    },
    propSpokeId: {
      type: String,
      default: null,
    },
    propUserId: {
      type: String,
      default: null,
    },
    propBckColor: {
      type: String,
      default: null,
    },
  },
  watch: {
    chatInterfaceOpen() {
      this.sendMessageToParent();
    },
  },
  mounted() {
    this.sendMessageToParent();
  },
  data() {
    return {
      conversationId: null,
      retryCount: 0,
      chatInterfaceOpen: false,
      chatInterfaceMinimized: false,
      loading: true,
      messages: [],
      inputMessage: "",
      firstMessage: "",
      spokeId: "",
      spokeName: "",
      bckColor: "#2E012E",
      sendingMessage: false,
    };
  },
  async created() {
    apiClient.defaults.headers["API-Key"] =
      (this.propApiKey ?? this.$route.query.apiKey).split("-").pop();
    this.spokeId = this.propSpokeId ?? this.$route.query.spokeId;
    this.userId = this.propUserId ?? this.$route.query.userId;
    this.getSpokeData();
  },
  computed: {
    widgetColor() {
      return this.bckColor;
    },
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer.$el;
        container.scrollTop = container.scrollHeight;
      });
    },
    sendMessageToParent() {
      if (window.parent !== window) {
        window.setTimeout(() => {
          const floatingWidget = document.querySelector("#floating-widget");
          let height, width;
          if (floatingWidget.classList.contains("open")) {
            // If widget is open, set height and width to 600
            height = 600;
            width = 600;
          } else {
            // If widget is closed, set height and width to 50
            height = 50;
            width = 50;
          }
          //console.log(`Resizing: ${width}x${height}`);
          window.parent.postMessage(
            {
              type: "resizeIframe",
              height: height,
              width: width,
            },
            "*"
          );
        }, 100);
      }
    },
    async createConversation() {
      this.loading = true;
      this.chatInterfaceOpen = true;
      try {
        //console.log(`creating conversation for ${this.spokeId}`);
        const response = await apiClient.post(
          "https://europe-west1-yggdrasil-ai-hermod.cloudfunctions.net/api-rest/create-conversation",
          {
            spokeId: this.spokeId,
          }
        );
        //this.messages = [{author: this.spokesId, content: response.data.responseMessage, role: "assistant"}];
        this.conversationId = response.data.payload.conversationId;
        this.getConversationData();
      } catch (error) {
        console.error("Error creating conversation:", error);
        this.chatInterfaceOpen = false;
        this.loading = false;
      }
    },
    async getSpokeData() {
      try {
        const apiKey = this.propApiKey ?? this.$route.query.apiKey;
        const userId = this.userId ?? apiKey.substring(0, apiKey.indexOf("-"));
        const response = await apiClient.post(
          "https://getspokedata-ai32xjq4va-uc.a.run.app",
          {
            data: { spokeId: this.spokeId, userId: userId },
          }
        );
        if (response.data) {
          //console.log("response.data: ", response.data)
          this.bckColor = response.data.color ?? "#2E012E";
          this.spokeName = response.data.name;
          this.firstMessage = response.data.startingMessage;
          this.messages.push({
            author: this.spokeId,
            content: this.firstMessage,
            role: "assistant",
          });
          this.sendingMessage = false;
          if (response.data.selectedStyle === "image") {
            const newImageRef = ref(storage, response.data.imageUrl);
            this.buttonImage = await getDownloadURL(newImageRef);
          } else if (response.data.selectedStyle === "robot") {
            this.animation = true;
            this.animationIndex = response.data.selectedRobotAnimationIndex;
          }
        }
      } catch (error) {
        console.error("Error creating conversation:", error);
      } finally {
        this.loading = false;
      }
    },
    async getConversationData() {
      //this.chatInterfaceOpen = true;
      try {
        //console.log(`loading conversation data for ${this.spokeId}`);
        const response = await apiClient.get(
          `https://europe-west1-yggdrasil-ai-hermod.cloudfunctions.net/api-rest/conversations/${this.conversationId}/messages`
        );
        this.messages = response.data;
        this.sendMessageToParent();
      } catch (error) {
        if (this.retryCount < 10) {
          // Limit the number of retries
          setTimeout(() => {
            this.retryCount++;
            this.$nextTick(() => {
              this.getConversationData();
            });
          }, 1000); // Wait for 1 second before retrying
        } else {
          console.error("Error retrieving conversation data:", error);
          this.retryCount = 0;
        }
      } finally {
        this.sendingMessage = false;
        this.loading = false;
      }
    },
    async sendMessage() {
      if (this.inputMessage.trim() === "" || this.loading) {
        return;
      }
      this.sendingMessage = true;
      this.messages.push({
        author: "allUsers",
        content: this.inputMessage,
        role: "user",
      });
      const newMessage = this.inputMessage;
      this.inputMessage = "";
      this.scrollToBottom();
      this.sendMessageToParent();

      try {
        const response = await apiClient.post(
          `https://europe-west1-yggdrasil-ai-hermod.cloudfunctions.net/api-rest/conversations/${this.conversationId}/messages`,
          {
            message: newMessage,
          }
        );

        this.messages.push({
          author: response.data.author,
          content: response.data.message,
          role: "assistant",
        });
        this.scrollToBottom();
        this.sendMessageToParent();
      } catch (error) {
        console.error("Error sending message:", error);
        //this.inputMessage = "ERROR";
      } finally {
        this.sendingMessage = false;
      }
    },
    async closeChat() {
      this.chatInterfaceOpen = false;
      this.loading = true;
      this.sendMessageToParent();

      try {
        await apiClient.delete(
          `https://europe-west1-yggdrasil-ai-hermod.cloudfunctions.net/api-rest/conversations/${this.conversationId}`
        );

        this.conversationId = null;
        this.messages = [
          {
            author: this.spokeId,
            content: this.firstMessage,
            role: "assistant",
          },
        ];
        this.loading = false;
      } catch (error) {
        console.error("Error deleting conversation:", error);
      }
    },
    minimizeChat() {
      this.chatInterfaceMinimized = true;
      this.chatInterfaceOpen = false;
    },
    restoreChat() {
      this.chatInterfaceMinimized = false;
      this.chatInterfaceOpen = true;
    },
  },
};
</script>

<style scoped>
.loading span {
  display: inline-block;
  margin-right: 2px;
  opacity: 0;
  animation: loading 1s infinite;
}

.loading span:nth-child(1) {
  animation-delay: 0s;
}

.loading span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes loading {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

html,
body {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}

.custom-image-button {
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.button-container {
  background-color: transparent;
}

.widget-container:not(.open) {
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  max-width: 100px !important;
  max-height: 100px !important;
}

.widget-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  width: 95%; /* Adjust this value to match the button width when the chat is closed */
  height: 90%;
  transition: none;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  padding: 1rem 1.5rem; /* Add extra padding to the top and bottom */
}

.widget-container.open {
  width: 90%; /* Adjust this value to match the desired width when the chat is open */
  max-height: 100%;
  transition: max-width 0.3s ease-in-out;
}

.messages-container {
  margin-top: 3rem;
  overflow-y: auto;
  height: 100%;
  min-height: 50vh;
  max-height: 50vh;
  background-color: #f7f2f7;
  border-bottom: 1px solid #ccc;
}

@media screen and (min-height: 520px) and (max-height: 579px) {
  .messages-container {
    min-height: 60vh;
    max-height: 60vh;
  }
}

@media screen and (min-height: 580px) {
  .messages-container {
    min-height: 63vh;
    max-height: 63vh;
  }
}

.message {
  width: 85%;
}

.small-message {
  width: 20%;
}

.markdown-container :deep(pre) {
  background-color: #e8e8e8;
  padding: 1rem;
  border-radius: 5px;
  overflow-x: auto;
}

.markdown-container :deep(table) {
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.markdown-container :deep(table) th,
.markdown-container :deep(table) td {
  border: 1px solid #ccc;
  padding: 0.5rem;
  text-align: left;
}
.markdown-container :deep(ul),
.markdown-container :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.markdown-container :deep(li) {
  margin-bottom: 0.5rem;
}

.markdown-container :deep(ul) ul,
.markdown-container :deep(ul) ol,
.markdown-container :deep(ol) ul,
.markdown-container :deep(ol) ol {
  margin-top: 0.5rem;
  margin-bottom: 0;
}

/* Headings */
.markdown-container :deep(h1),
.markdown-container :deep(h2),
.markdown-container :deep(h3),
.markdown-container :deep(h4),
.markdown-container :deep(h5),
.markdown-container :deep(h6) {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

/* Blockquotes */
.markdown-container :deep(blockquote) {
  padding-left: 1rem;
  border-left: 3px solid #ccc;
  margin-top: 1rem;
  margin-bottom: 1rem;
  font-style: italic;
}

/* Inline code */
.markdown-container :deep(code) {
  background-color: #e8e8e8;
  border-radius: 3px;
  padding: 0.2rem 0.4rem;
  font-size: 0.9em;
}

/* Links */
.markdown-container :deep(a) {
  color: #3498db;
  text-decoration: none;
}

.markdown-container :deep(a:hover) {
  text-decoration: underline;
}

/* Horizontal rule */
.markdown-container :deep(hr) {
  border: 0;
  height: 1px;
  background-color: #ccc;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.powered-by {
  display: none;
  font-size: 0.8rem;
  margin-top: 2px;
  bottom: 0px;
  margin-right: -10px;
}

.powered-by-icon {
  width: 20px;
  height: 20px;
  vertical-align: middle;
}

.powered-by-link {
  color: #888;
  margin-left: 0.25rem;
  text-decoration: none;
  font-size: 11px;
  transition: 200ms ease-in-out;
}

.powered-by-link:hover {
  opacity: 0.7;
}

.widget-container.open .powered-by {
  display: block;
}

.close-button {
  position: absolute;
  right: 10px;
  top: 10px;
}

.minimize-button {
  position: absolute;
  right: 55px;
  top: 10px;
}

.restore-conversation-button {
  position: fixed;
  right: 20px;
  bottom: 10px;
}

.send-button {
  margin-top: 2rem;
}

.bottom-area {
  position: absolute;
  bottom: 0px;
  width: 100%;
  padding: 0.1rem 1rem;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.top-area {
  position: absolute;
  top: 0px;
  width: 100%;
  border-bottom: 1px solid #ccc;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.text-input {
  margin-top: 5px;
}

.v-btn.v-btn--disabled {
  background-color: initial !important;
  color: gray !important;
  opacity: 0.5 !important;
}
</style>
